/**
 * Time Range Utilities for Timeline Components
 *
 * These utilities help manage timeline slot selection with intelligent range building.
 *
 * Usage Examples:
 *
 * 1. Using createSlotClickHandler (recommended for most cases):
 * ```javascript
 * import { createSlotClickHandler } from '../../utils/timeRangeUtils';
 *
 * const [timeRangeFirstDay, setTimeRangeFirstDay] = useState(null);
 * const [timeRangeSecondDay, setTimeRangeSecondDay] = useState(null);
 *
 * // Create handlers for each timeline
 * const firstDayHandler = createSlotClickHandler(setTimeRangeFirstDay, timeRangeFirstDay);
 * const secondDayHandler = createSlotClickHandler(setTimeRangeSecondDay, timeRangeSecondDay);
 *
 * // Use in JSX
 * <Timeline handleTimeSlotClick={firstDayHandler} tempTimeRange={timeRangeFirstDay} />
 * <Timeline handleTimeSlotClick={secondDayHandler} tempTimeRange={timeRangeSecondDay} />
 * ```
 *
 * 2. Using updateTimeRangeWithSlot directly:
 * ```javascript
 * import { updateTimeRangeWithSlot } from '../../utils/timeRangeUtils';
 *
 * const handleSlotClick = (slot) => {
 *   const updatedRange = updateTimeRangeWithSlot(currentTimeRange, slot);
 *   setTimeRange(updatedRange);
 * };
 * ```
 */

/**
 * Helper function to manage timeline slot selection
 * Handles the logic for creating/updating time ranges based on slot clicks
 *
 * Logic:
 * - First click: Creates a range with start=slot.start, end=slot.end
 * - Subsequent clicks:
 *   - If new slot is before current start: Updates start to new slot.start
 *   - If new slot is after current end: Updates end to new slot.end
 *   - If new slot is within current range: Resets to just the new slot
 *
 * @param {Object} currentTimeRange - Current time range object {start, end}
 * @param {Object} clickedSlot - The slot that was clicked
 * @param {string} clickedSlot.start - Start time of the clicked slot (e.g., "09:00")
 * @param {string} clickedSlot.end - End time of the clicked slot (e.g., "09:30")
 * @returns {Object} Updated time range object {start, end}
 */
export const updateTimeRangeWithSlot = (currentTimeRange, clickedSlot) => {
  // If no current range exists, create a new one with the clicked slot
  if (!currentTimeRange || !currentTimeRange.start) {
    return {
      start: clickedSlot.start,
      end: clickedSlot.end,
    };
  }

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString
      .split(":")
      .map((num) => parseInt(num, 10));
    return hours * 60 + (minutes || 0);
  };

  const currentStartMinutes = timeToMinutes(currentTimeRange.start);
  const currentEndMinutes = timeToMinutes(currentTimeRange.end);
  const clickedStartMinutes = timeToMinutes(clickedSlot.start);
  const clickedEndMinutes = timeToMinutes(clickedSlot.end);

  // If clicked slot is before the current start, update the start
  if (clickedStartMinutes < currentStartMinutes) {
    return {
      start: clickedSlot.start,
      end: currentTimeRange.end,
    };
  }

  // If clicked slot is after the current end, update the end
  if (clickedEndMinutes > currentEndMinutes) {
    return {
      start: currentTimeRange.start,
      end: clickedSlot.end,
    };
  }

  // If clicked slot is within the current range, reset to just the clicked slot
  // This allows users to "restart" the selection
  return {
    start: clickedSlot.start,
    end: clickedSlot.end,
  };
};

/**
 * Factory function to create a time range handler for any timeline
 * This can be used to create handlers for multiple timelines on the same screen
 *
 * @param {Function} setTimeRange - State setter function for the time range
 * @returns {Function} Handler function that can be passed to Timeline components
 */
export const createTimeRangeHandler = (setTimeRange) => {
  return (currentTimeRange, clickedSlot) => {
    const updatedRange = updateTimeRangeWithSlot(currentTimeRange, clickedSlot);
    setTimeRange(updatedRange);
  };
};

/**
 * Higher-order function to create a slot click handler for Timeline components
 *
 * @param {Function} setTimeRange - State setter function for the time range
 * @param {Object} currentTimeRange - Current time range state
 * @returns {Function} Click handler function
 */
export const createSlotClickHandler = (setTimeRange, currentTimeRange) => {
  return (slot) => {
    const updatedRange = updateTimeRangeWithSlot(currentTimeRange, slot);
    setTimeRange(updatedRange);
  };
};

/**
 * Helper function to check if a slot is within a time range
 *
 * @param {Object} slot - The slot to check
 * @param {Object} timeRange - The time range to check against
 * @returns {boolean} True if the slot is within the range
 */
export const isSlotInRange = (slot, timeRange) => {
  if (!timeRange || !timeRange.start || !timeRange.end) return false;

  return slot.start >= timeRange.start && slot.start < timeRange.end;
};

/**
 * Helper function to format time range for display
 *
 * @param {Object} timeRange - The time range object
 * @returns {string} Formatted time range string
 */
export const formatTimeRange = (timeRange) => {
  if (!timeRange || !timeRange.start || !timeRange.end) return "";

  return `${timeRange.start} - ${timeRange.end}`;
};
